import apiClient from '../../../../shared/api/apiClient'
import { ENDPOINTS } from '../../../../shared/api/endpoints'
import type {
  AttendanceRegularizationItem,
  AttendanceRegularizationsQueryParams,
  AttendanceRegularizationsResponse,
  ReviewAttendanceRegularizationPayload,
} from '../types/hrmsRegularizationReview.types'

/**
 * Normalizes query parameters for attendance regularizations API.
 * All empty or undefined values are omitted.
 */
export function buildAttendanceRegularizationsQueryParams(
  params?: AttendanceRegularizationsQueryParams
): Record<string, string | number> {
  const query: Record<string, string | number> = {}

  if (params?.status) {
    query.status = params.status
  }
  if (params?.staffId && params.staffId.trim()) {
    query.staffId = params.staffId.trim()
  }
  if (params?.departmentId && params.departmentId.trim()) {
    query.departmentId = params.departmentId.trim()
  }
  if (params?.search && params.search.trim()) {
    query.search = params.search.trim()
  }
  if (params?.fromDate && params.fromDate.trim()) {
    query.fromDate = params.fromDate.trim()
  }
  if (params?.toDate && params.toDate.trim()) {
    query.toDate = params.toDate.trim()
  }
  if (typeof params?.page === 'number') {
    query.page = params.page
  }
  if (typeof params?.limit === 'number') {
    query.limit = params.limit
  }

  return query
}

/**
 * 1) GET ATTENDANCE_REGULARIZATIONS (paginated list)
 */
export async function getAttendanceRegularizations(
  params?: AttendanceRegularizationsQueryParams
): Promise<AttendanceRegularizationsResponse> {
  const query = buildAttendanceRegularizationsQueryParams(params)

  const response = await apiClient.get<unknown>(ENDPOINTS.HRMS.ATTENDANCE_REGULARIZATIONS, {
    params: Object.keys(query).length > 0 ? query : undefined,
  })

  const payload = (response.data as { data?: unknown })?.data ?? response.data
  const payloadObj = payload as {
    items?: unknown
    total?: unknown
    page?: unknown
    limit?: unknown
  }

  const items: AttendanceRegularizationItem[] = Array.isArray(payload)
    ? (payload as AttendanceRegularizationItem[])
    : Array.isArray(payloadObj?.items)
      ? (payloadObj.items as AttendanceRegularizationItem[])
      : []

  const total = typeof payloadObj?.total === 'number' ? payloadObj.total : items.length
  const page = typeof payloadObj?.page === 'number' ? payloadObj.page : (params?.page || 1)
  const limit = typeof payloadObj?.limit === 'number' ? payloadObj.limit : (params?.limit || 20)

  return {
    items,
    total,
    page,
    limit,
  }
}

/**
 * 2) GET ATTENDANCE_REGULARIZATION_BY_ID(id) -> single item
 */
export async function getAttendanceRegularizationById(
  id: string
): Promise<AttendanceRegularizationItem> {
  if (!id || !id.trim()) {
    throw new Error('Attendance regularization ID is required')
  }

  const response = await apiClient.get<unknown>(
    ENDPOINTS.HRMS.ATTENDANCE_REGULARIZATION_BY_ID(id.trim())
  )

  const payload = (response.data as { data?: unknown })?.data ?? response.data
  return payload as AttendanceRegularizationItem
}

/**
 * 3) PATCH ATTENDANCE_REGULARIZATION_REVIEW(id)
 * Body: { status: 'APPROVED' | 'REJECTED', reviewerRemarks?, rejectionReason? }
 */
export async function reviewAttendanceRegularization(
  id: string,
  payload: ReviewAttendanceRegularizationPayload
): Promise<AttendanceRegularizationItem> {
  if (!id || !id.trim()) {
    throw new Error('Attendance regularization ID is required')
  }

  const body: Record<string, string> = {
    status: payload.status,
  }

  if (payload.status === 'REJECTED') {
    if (!payload.rejectionReason?.trim()) {
      throw new Error('Rejection reason is required when rejecting a regularization request')
    }
    body.rejectionReason = payload.rejectionReason.trim()
  }

  if (payload.reviewerRemarks?.trim()) {
    body.reviewerRemarks = payload.reviewerRemarks.trim()
  }

  const response = await apiClient.patch<unknown>(
    ENDPOINTS.HRMS.ATTENDANCE_REGULARIZATION_REVIEW(id.trim()),
    body
  )

  const res = (response.data as { data?: unknown })?.data ?? response.data
  return res as AttendanceRegularizationItem
}
